'use client';

import { ColumnDef } from '@tanstack/react-table';
import { CalendarIcon, CircleDashed, CreditCard, DollarSign, Ellipsis, Hash } from 'lucide-react';
import * as React from 'react';
import { Link, useParams } from 'react-router-dom';

import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { DataTableCheckbox } from '@/components/data-table/data-table-checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Payment } from '@tagadapay/crm-entities';
import { DateDisplay } from '@/components/date-display';
import { formatMoney } from '@/lib/money';
import { PaymentStatusBadge } from '@/components/payments/payment-status-badge';
import { InitiatedByBadge } from '@/components/payments/initiated-by-badge';
import {
  getCryptoOnchainAssetLabel,
  getPaymentMethodDisplay,
  PaymentMethodCell,
  resolveWalletType,
} from '@/components/payments/payment-method-cell';
import { useStoreFilterOptions } from '@/lib/api/stores';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import { useOrgNavigate } from '@/hooks/use-app-navigate';

// Component for org-aware navigation links
function ViewDetailsLink({
  paymentId,
  label,
  className,
}: {
  paymentId: string;
  label: string;
  className?: string;
}) {
  const { orgRoutes } = useOrgNavigate();
  return (
    <Link to={orgRoutes.PAYMENT_DETAILS(paymentId)} className={className}>
      {label}
    </Link>
  );
}

interface GetPaymentsTableColumnsProps {
  onViewDetails: (payment: Payment.Payment) => void;
  onRefund: (payment: Payment.Payment) => void;
  options: {
    status: string[];
    currency: string[];
    initiatedBy: string[];
    reasonType: string[];
    requireAction: string[];
    processorName: string[];
    paymentMethod: string[];
  };
  t: (key: string, v?: Record<any, any>) => string;
}

/**
 * Get payment table columns with filters and sorting
 */
export function getPaymentsTableColumns({
  onViewDetails,
  onRefund,
  options,
  t,
}: GetPaymentsTableColumnsProps): ColumnDef<Payment.Payment>[] {
  return [
    // 1. Select checkbox
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label={t('table.selectAll')}
          className="translate-y-0.5"
        />
      ),
      cell: ({ row }) => <DataTableCheckbox row={row} />,
      enableSorting: false,
      enableHiding: false,
      size: 40,
    },
    // 2. Amount (moved to top)
    {
      id: 'amount',
      accessorKey: 'amount',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.amount')} />,
      cell: ({ row }) => {
        const amount = row.original.amount;
        const currency = row.original.currency || 'USD';

        return (
          <div className="flex flex-col gap-0.5">
            <div className="text-sm font-semibold tabular-nums">{formatMoney(amount, currency)}</div>
            {currency !== 'USD' && (
              <div className="text-muted-foreground text-xs font-medium">{currency}</div>
            )}
          </div>
        );
      },
      meta: {
        label: t('table.columns.amount'),
        variant: 'money',
        icon: () => <DollarSign />,
        options: [
          { label: t('table.filters.moneyOptions.isEqualTo'), value: 'is-equal-to' },
          { label: t('table.filters.moneyOptions.isBetween'), value: 'is-between' },
          { label: t('table.filters.moneyOptions.isGreaterThan'), value: 'is-greater-than' },
          { label: t('table.filters.moneyOptions.isLessThan'), value: 'is-less-than' },
        ],
        formatValue: (minorUnits: number) => formatMoney(minorUnits, 'USD'),
        currency: 'USD',
      },
      filterFn: () => true, // Passthrough since filtering is manual/server-side
      enableColumnFilter: true,
      enableSorting: true,
      size: 120,
    },
    // 3. Customer
    {
      id: 'customer',
      accessorKey: 'customer.email',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.customer')} />,
      cell: ({ row }) => {
        const email = row.original.customer?.email;
        const name = row.original.customer?.fullName;
        const displayName = name && name !== '-' ? name : '—';
        const displayEmail = email && email !== '-' ? email : '—';

        return (
          <div className="flex min-w-0 flex-col gap-0.5 py-1">
            <div className="truncate text-sm font-medium leading-none">{displayName}</div>
            <div className="text-muted-foreground truncate text-xs leading-none">{displayEmail}</div>
          </div>
        );
      },
      meta: {
        label: t('table.columns.customer'),
      },
      enableColumnFilter: false, // Use search bar for customer search (full-text search)
      enableSorting: false,
      size: 200,
    },
    // 4. Status
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.status')} />,
      cell: ({ row }) => {
        const status = row.original.status as any;
        const subStatus = (row.original as any).subStatus;

        return <PaymentStatusBadge status={status} subStatus={subStatus} />;
      },
      meta: {
        label: t('table.columns.status'),
        variant: 'multiSelect',
        options: options.status.map((status) => ({
          label: t(`table.filters.status.${status}`),
          value: status,
        })),
        icon: () => <CircleDashed />,
      },
      enableColumnFilter: true,
      enableSorting: true,
    },
    // 6. Payment Method (moved up - key info)
    {
      id: 'card',
      accessorKey: 'paymentInstrument.card',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('table.columns.paymentMethod')} />
      ),
      cell: ({ row }) => <PaymentMethodCell payment={row.original} isAssetHidden />,
      meta: {
        label: t('table.columns.paymentMethod'),
        variant: 'text',
        placeholder: t('table.filters.last4Placeholder'),
        icon: () => <CreditCard />,
        filterWidth: 'w-32',
      },
      enableColumnFilter: true,
      enableSorting: false,
    },
    // 6a. Payment Type (filter-only - apple_pay / google_pay / card / crypto)
    {
      id: 'paymentMethod',
      accessorKey: 'paymentInstrument.type',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('table.columns.paymentType')} />
      ),
      cell: ({ row }) => {
        // On-chain crypto: the coin and the network, `ETH (Ethereum Sepolia)`.
        const display = getPaymentMethodDisplay(row.original);
        if (display.kind === 'crypto') {
          return (
            <Badge variant="outline" className="font-medium">
              {getCryptoOnchainAssetLabel(display) ?? t('table.filters.paymentMethod.crypto')}
            </Badge>
          );
        }
        const type = row.original.paymentInstrument?.type;
        if (!type) return <span className="text-muted-foreground text-sm">—</span>;
        // Stripe express rows (`apm_stripe_apple_pay`) read as their wallet,
        // same as the method cell (#TAG-2239).
        const normalized = resolveWalletType(type) ?? type;
        const known = ['apple_pay', 'google_pay', 'card'].includes(normalized);
        return (
          <Badge variant="outline" className="font-medium">
            {known ? t(`table.filters.paymentMethod.${normalized}`) : type}
          </Badge>
        );
      },
      meta: {
        label: t('table.columns.paymentType'),
        variant: 'multiSelect',
        options: options.paymentMethod.map((m) => ({
          label: t(`table.filters.paymentMethod.${m}`),
          value: m,
        })),
        icon: () => <CreditCard />,
      },
      enableColumnFilter: true,
      enableSorting: false,
      enableHiding: true,
    },
    // 6b. BIN (Card Enrichment)
    {
      id: 'bin',
      accessorKey: 'paymentInstrument.card.bin',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.bin')} />,
      cell: ({ row }) => <div>{row.getValue('bin')}</div>, // Filter-only column, no cell display
      meta: {
        label: t('table.columns.bin'),
        variant: 'text',
        placeholder: t('table.filters.binPlaceholder'),
        icon: () => <Hash />,
        filterWidth: 'w-32',
      },
      enableColumnFilter: true,
      enableSorting: false,
      enableHiding: true,
      // size: 0, // Hidden column (filter-only)
    },
    // 7. Date (moved up - important)
    {
      id: 'createdAt',
      accessorKey: 'createdAt',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.date')} />,
      cell: ({ row }) => (
        <DateDisplay
          value={row.getValue('createdAt') as string}
          mode="split"
          className="text-sm font-medium tabular-nums leading-none py-1"
        />
      ),
      meta: {
        label: t('table.columns.date'),
        variant: 'dateRange',
        icon: () => <CalendarIcon />,
      },
      enableColumnFilter: true,
      enableSorting: true,
      size: 130,
    },
    // 8. Store (secondary info)
    {
      id: 'store',
      accessorKey: 'store.name',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.store')} />,
      cell: ({ row }) => {
        const store = (row.original as any).store;
        const storeName = store?.name;

        if (!storeName) {
          return <span className="text-muted-foreground text-sm">—</span>;
        }

        return (
          <div className="flex items-center gap-1.5">
            <div className="truncate text-sm font-medium">{storeName}</div>
          </div>
        );
      },
      meta: {
        label: t('table.columns.store'),
        variant: 'multiAsyncSelect',
        asyncFilterOptions: useStoreFilterOptions,
      },
      enableColumnFilter: true,
      enableSorting: false,
      enableHiding: true,
      size: 150,
    },
    // 9. Currency (moved down - less critical)
    {
      id: 'currency',
      accessorKey: 'currency',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.currency')} />,
      cell: ({ row }) => {
        const currency = row.original.currency;
        return (
          <div className="flex items-center">
            <Badge variant="outline" className="font-mono text-xs font-medium">
              {currency}
            </Badge>
          </div>
        );
      },
      meta: {
        label: t('table.columns.currency'),
        variant: 'multiSelect',
        options: options.currency.map((curr) => ({
          label: curr,
          value: curr,
        })),
      },
      enableColumnFilter: true,
      enableSorting: true,
      enableHiding: true,
      size: 100,
    },
    // 10. Processor (secondary info)
    {
      id: 'processor',
      accessorKey: 'processor',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.processor')} />,
      cell: ({ row }) => {
        const payment = row.original as any;
        const processor = payment.processorName || payment.processor?.name;

        if (processor) {
          return (
            <Badge variant="secondary" className="font-medium">
              {processor}
            </Badge>
          );
        }

        // On-chain crypto runs on no processor of the account: crypto-api settles it.
        if (getPaymentMethodDisplay(payment).kind === 'crypto') {
          return (
            <Badge variant="outline" className="font-medium">
              {t('table.fallbacks.cryptoOnchain')}
            </Badge>
          );
        }

        // Integration payments (HiPay, ConvesioPay, etc.) have no processor
        const createdBy = payment.metadata?.createdBy as string | undefined;
        if (typeof createdBy === 'string' && createdBy.startsWith('integration:')) {
          if (createdBy === 'integration:custom_payment') {
            const snapshot = payment.metadata?.manualPaymentSnapshot as { displayName?: string } | undefined;
            const name = snapshot?.displayName || t('table.fallbacks.customPayment');
            return (
              <Badge variant="outline" className="font-medium">
                {name}
              </Badge>
            );
          }
          const provider = createdBy.replace('integration:', '');
          const label = provider.charAt(0).toUpperCase() + provider.slice(1);
          return (
            <Badge variant="outline" className="font-medium">
              {t('table.fallbacks.integration', { provider: label })}
            </Badge>
          );
        }

        return <span className="text-muted-foreground text-sm">—</span>;
      },
      meta: {
        label: t('table.columns.processor'),
        variant: 'multiSelect',
        options: options.processorName.map((name) => ({
          label: name,
          value: name,
        })),
      },
      enableColumnFilter: true,
      enableSorting: false,
      enableHiding: true,
      size: 130,
    },
    // 11. Initiated By (advanced - hideable)
    {
      id: 'initiatedBy',
      accessorKey: 'initiatedBy',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('table.columns.initiatedBy')} />
      ),
      cell: ({ row }) => {
        const initiatedBy = (row.original as any).initiatedBy;
        return <InitiatedByBadge initiatedBy={initiatedBy} />;
      },
      meta: {
        label: t('table.columns.initiatedBy'),
        variant: 'multiSelect',
        options: options.initiatedBy.map((val) => ({
          label: t(`table.filters.initiatedBy.${val}`),
          value: val,
        })),
      },
      enableColumnFilter: true,
      enableSorting: true,
      enableHiding: true,
    },
    // 12. Chain Index (advanced - hideable)
    {
      id: 'chainIndex',
      accessorKey: 'chainIndex',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.chainIndex')} />,
      cell: ({ row }) => {
        const chainIndex = (row.original as any).chainIndex;
        return (
          <div className="flex items-center justify-center">
            <Badge variant="outline" className="font-mono text-xs">
              {chainIndex ?? 0}
            </Badge>
          </div>
        );
      },
      meta: {
        label: t('table.columns.chainIndex'),
        variant: 'range',
        range: [0, 20],
      },
      enableColumnFilter: true,
      enableSorting: true,
      enableHiding: true,
      size: 100,
    },
    // 13. Retry Index (advanced - hideable)
    {
      id: 'retryIndex',
      accessorKey: 'retryIndex',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.retryIndex')} />,
      cell: ({ row }) => {
        const retryIndex = (row.original as any).retryIndex;
        return (
          <div className="flex items-center justify-center">
            <Badge variant="outline" className="font-mono text-xs">
              {retryIndex ?? 0}
            </Badge>
          </div>
        );
      },
      meta: {
        label: t('table.columns.retryIndex'),
        variant: 'range',
        range: [0, 20],
      },
      enableColumnFilter: true,
      enableSorting: true,
      enableHiding: true,
      size: 100,
    },
    // 14. Reason Type (advanced - hideable)
    {
      id: 'reasonType',
      accessorKey: 'reasonType',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.reasonType')} />,
      cell: ({ row }) => {
        const reasonType = (row.original as any).reasonType;

        if (!reasonType) {
          return <span className="text-muted-foreground text-sm">—</span>;
        }

        return (
          <Badge variant="outline" className="font-medium">
            {t(`table.filters.reasonType.${reasonType}`)}
          </Badge>
        );
      },
      meta: {
        label: t('table.columns.reasonType'),
        variant: 'multiSelect',
        options: options.reasonType.map((val) => ({
          label: t(`table.filters.reasonType.${val}`),
          value: val,
        })),
      },
      enableColumnFilter: false,
      enableSorting: false,
      enableHiding: true,
      size: 130,
    },
    // 15. Action Required (advanced - hideable)
    {
      id: 'requireAction',
      accessorKey: 'requireAction',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('table.columns.actionRequired')} />
      ),
      cell: ({ row }) => {
        const requireAction = (row.original as any).requireAction;

        if (!requireAction || requireAction === 'none') {
          return <span className="text-muted-foreground text-sm">—</span>;
        }

        return (
          <Badge variant="destructive" className="font-medium">
            {t(`table.filters.requireAction.${requireAction}`)}
          </Badge>
        );
      },
      meta: {
        label: t('table.columns.actionRequired'),
        variant: 'multiSelect',
        options: options.requireAction.map((val) => ({
          label: t(`table.filters.requireAction.${val}`),
          value: val,
        })),
      },
      enableColumnFilter: false,
      enableSorting: false,
      enableHiding: true,
      size: 150,
    },
    // 16. Payment ID (moved to end, copyable)
    {
      id: 'id',
      accessorKey: 'id',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.paymentId')} />,
      cell: ({ row }) => {
        const payment = row.original;
        const displayValue = `${payment.id.substring(0, 8)}...`;
        return <CopyableResourceBadge name={t('table.columns.paymentId')} value={displayValue} copyValue={payment.id} />;
      },
      enableSorting: true,
      enableHiding: true,
      size: 140,
    },
    // 17. Actions
    {
      id: 'actions',
      cell: ({ row }) => {
        const payment = row.original;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                aria-label={t('table.openMenu')}
                variant="ghost"
                className="data-[state=open]:bg-muted flex h-8 w-8 p-0"
              >
                <Ellipsis className="h-4 w-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[160px]">
              <DropdownMenuItem asChild>
                <ViewDetailsLink paymentId={payment.id} label={t('table.actions.viewDetails')} />
              </DropdownMenuItem>
              {payment.isRefundable && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onRefund(payment)}
                    className="text-destructive focus:text-destructive"
                  >
                    {t('table.actions.refundPayment')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
      enableSorting: false,
      enableHiding: false,
      size: 40,
    },
  ];
}
